/** The steps an order flow can have, in the order the step list offers them (FR-CFG-02). */
export const STEP_TYPES = [
  'select_table',
  'enter_guests',
  'select_customer',
  'prompt',
  'take_order',
  'send_to_kitchen',
  'print_bill',
  'take_payment',
  'assign_driver',
  'close',
  // A website or app order's steps (ADR 0083): accept_order first; skipped by any other order.
  'accept_order',
  'mark_ready',
  'out_for_delivery',
  'hand_over',
  'deliver',
] as const;

export type StepType = (typeof STEP_TYPES)[number];

export interface StepRow {
  /** Stable while editing; the saved id for an existing step. */
  key: string;
  id?: string;
  stepType: string;
  isRequired: boolean;
}

export interface SavedStep {
  id: string;
  stepType: string;
  isRequired: boolean;
}

let counter = 0;
const newKey = () => `new-${++counter}`;

export const fromSteps = (steps: readonly SavedStep[]): StepRow[] =>
  steps.map((s) => ({ key: s.id, id: s.id, stepType: s.stepType, isRequired: s.isRequired }));

/** The steps of a preset, as new rows. */
export const fromPreset = (steps: readonly { stepType: string; isRequired: boolean }[]): StepRow[] =>
  steps.map((s) => ({ key: newKey(), stepType: s.stepType, isRequired: s.isRequired }));

export const addStep = (rows: StepRow[], stepType: string): StepRow[] => [
  ...rows,
  { key: newKey(), stepType, isRequired: true },
];

export const updateStep = (rows: StepRow[], key: string, patch: Partial<Pick<StepRow, 'stepType' | 'isRequired'>>) =>
  rows.map((r) => (r.key === key ? { ...r, ...patch } : r));

export const removeStep = (rows: StepRow[], key: string) => rows.filter((r) => r.key !== key);

/** Moves a step up (-1) or down (+1); at either end it stays. */
export function moveStep(rows: StepRow[], key: string, delta: -1 | 1): StepRow[] {
  const from = rows.findIndex((r) => r.key === key);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= rows.length) return rows;
  const next = [...rows];
  const moved = next[from];
  const other = next[to];
  if (!moved || !other) return rows;
  next[from] = other;
  next[to] = moved;
  return next;
}

/** Moves a step to the place another one holds, e.g. dropped there. */
export function moveStepTo(rows: StepRow[], key: string, targetKey: string): StepRow[] {
  const from = rows.findIndex((r) => r.key === key);
  const to = rows.findIndex((r) => r.key === targetKey);
  if (from < 0 || to < 0 || from === to) return rows;
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  if (!moved) return rows;
  next.splice(to, 0, moved);
  return next;
}

export type Problem = 'repeated' | 'empty' | 'acceptFirst';

/** A step can appear once, except a prompt; and a flow needs at least one step. */
export function problems(rows: readonly StepRow[]): Map<string, Problem> {
  const found = new Map<string, Problem>();
  const seen = new Set<string>();
  rows.forEach((r, index) => {
    if (r.stepType !== 'prompt' && seen.has(r.stepType)) found.set(r.key, 'repeated');
    // Nothing happens to an online order before the store accepts it (ADR 0083): accepting comes first, and is required.
    else if (r.stepType === 'accept_order' && (index > 0 || !r.isRequired)) found.set(r.key, 'acceptFirst');
    seen.add(r.stepType);
  });
  return found;
}

/** How many differences there are from what was saved: added, removed, changed and moved steps. */
export function changeCount(rows: readonly StepRow[], saved: readonly SavedStep[]): number {
  const byId = new Map(saved.map((s, i) => [s.id, { step: s, index: i }]));
  let count = 0;
  const kept = new Set<string>();
  rows.forEach((r, index) => {
    const original = r.id ? byId.get(r.id) : undefined;
    if (!original) {
      count++;
      return;
    }
    kept.add(original.step.id);
    if (original.step.stepType !== r.stepType || original.step.isRequired !== r.isRequired || original.index !== index)
      count++;
  });
  return count + saved.filter((s) => !kept.has(s.id)).length;
}

export const toRequest = (rows: readonly StepRow[]) => ({
  steps: rows.map((r) => ({ id: r.id ?? null, stepType: r.stepType, isRequired: r.isRequired })),
});
